import { useLang } from '../context/LanguageContext';
import LegalPageShell from '../components/LegalPageShell';
import { usePageMeta } from '../hooks/usePageMeta';
import { LEGAL_TE_SUMMARIES } from '../content/legalContent';
import { BUSINESS, businessAddressBlock, businessPhoneDisplay, businessTelHref } from '../config/business';

export function RefundContent() {
  return (
    <>
      <p className="legal-updated">Last updated: June 2026</p>

      <p className="legal-highlight">
        Full refund within 7 days if the e-paper was opened fewer than 3 times. Email{' '}
        <a href={`mailto:${BUSINESS.email}`}>{BUSINESS.email}</a> with your Razorpay payment ID.
      </p>

      <div className="legal-section">
        <h3>1. Refund Policy</h3>
        <p>We offer refunds under the following conditions:</p>
        <ul>
          <li>
            <strong>Within 7 days of purchase</strong> — Full refund if you have not accessed the e-paper more than
            3 times
          </li>
          <li>
            <strong>Technical issues</strong> — If you are unable to access the service due to a technical error on
            our end, a full refund will be issued
          </li>
          <li>
            <strong>Duplicate payment</strong> — If you were charged twice for the same subscription, the duplicate
            charge will be fully refunded
          </li>
        </ul>
      </div>

      <div className="legal-section">
        <h3>2. Non-Refundable Cases</h3>
        <p>Refunds will NOT be issued in the following cases:</p>
        <ul>
          <li>After 7 days from the date of purchase</li>
          <li>If the service has been actively used beyond the limits above</li>
          <li>Dissatisfaction with editorial content</li>
          <li>Internet connectivity issues on the user&apos;s end</li>
        </ul>
      </div>

      <div className="legal-section">
        <h3>3. How to Request a Refund</h3>
        <p>To request a refund, please contact us within 7 days of your purchase:</p>
        <ul>
          <li>
              Email: <strong>{BUSINESS.email}</strong>
              <br />
              Phone / WhatsApp: <a href={businessTelHref()}>{businessPhoneDisplay()}</a>
          </li>
          <li>Subject: Refund Request — [Your email]</li>
          <li>Include your Razorpay payment ID or receipt</li>
        </ul>
        <p>
          We will process your request within <strong>5–7 business days</strong>. Refunds will be credited to the
          original payment method used via Razorpay.
        </p>
      </div>

      <div className="legal-section">
        <h3>4. International Payment Refunds</h3>
        <p>
          If you paid with an international credit or debit card, refunds are issued in <strong>INR</strong> to the
          same card through Razorpay. Depending on your bank and country, the refund may take{' '}
          <strong>5–14 business days</strong> to appear. Currency conversion differences are determined by your card
          issuer — we do not control exchange rates or foreign transaction fees charged by your bank.
        </p>
      </div>

      <div className="legal-section">
        <h3>5. Payment Disputes</h3>
        <p>
          If you believe an incorrect charge has been made, please contact us before raising a dispute with your
          bank. We will resolve genuine issues promptly.
        </p>
      </div>

      <div className="legal-section">
        <h3>6. Cancellation</h3>
        <p>
          For subscription cancellation (stopping access / no renewal), see our separate{' '}
          <a href="/cancellation">Cancellation Policy</a>.
        </p>
      </div>

      <div className="legal-section">
        <h3>7. Contact</h3>
        <p>
          📧 <strong>{BUSINESS.email}</strong>
          <br />
          📍 {businessAddressBlock()}
          <br />
          GSTIN: {BUSINESS.gstin}
        </p>
      </div>
    </>
  );
}

export default function RefundPage({ onClose, standalone = false }) {
  const { t } = useLang();
  usePageMeta({
    title: `${t.footerRefund} | ${t.brandName}`,
    description: `Refund policy for Repatikosam digital subscription.`,
    path: '/refund',
    enabled: standalone,
  });
  return (
    <LegalPageShell
      title={t.footerRefund}
      onClose={onClose}
      standalone={standalone}
      teSummary={LEGAL_TE_SUMMARIES.refund}
    >
      <RefundContent />
    </LegalPageShell>
  );
}
